import { Order } from '@/data/mockData';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getOutputFormatLabel } from '@/constants/outputFormat';

interface OrderInfoDialogProps {
  order: Order | null;
  onOpenChange: (open: boolean) => void;
}

// Libellé du type de contact d'une population, identique à celui affiché dans le
// formulaire de demande (basé sur le id_type brut de l'API).
const populationTypeLabel = (idType: string) => {
  switch (idType) {
    case 'emails':
      return 'Emails';
    case 'phones':
      return 'Mobiles';
    case 'emails OR phones':
      return 'Emails ou Mobiles';
    case 'emails AND phones':
      return 'Emails et Mobiles';
    default:
      return idType;
  }
};

export const OrderInfoDialog = ({
  order,
  onOpenChange,
}: OrderInfoDialogProps) => {
  if (!order) return null;

  // Grouper les segments par catégorie
  const segmentsByCategory = order.selectedSegments?.reduce((acc, seg) => {
    if (!acc[seg.categoryName]) {
      acc[seg.categoryName] = [];
    }
    acc[seg.categoryName].push(seg);
    return acc;
  }, {} as Record<string, typeof order.selectedSegments>) || {};

  return (
    <Dialog open={!!order} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Détails de la demande</DialogTitle>
          <DialogDescription className="text-base font-semibold text-foreground mt-2">
            {order.requestName}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* CRM sélectionné */}
          {order.selectedCrm && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">CRM sélectionné :</span>
                <span className="font-semibold">{order.selectedCrm}</span>
              </div>
            </div>
          )}

          {/* Format du fichier de sortie (lecture seule) */}
          {order.outputFormat && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">Format du fichier de sortie :</span>
                <span className="font-semibold">{getOutputFormatLabel(order.outputFormat)}</span>
              </div>
            </div>
          )}

          {/* CRM exclus */}
          {order.crmExclusion && order.crmExclusion.length > 0 && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="font-medium mb-2">CRM exclus :</div>
              <ul className="space-y-1">
                {order.crmExclusion.map((file) => {
                  const label = file.toLowerCase().endsWith('.csv') ? file.slice(0, -4) : file;
                  return (
                    <li key={file} className="text-sm">• {label}</li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Populations (détail par population : type de contact + volume demandé) */}
          {order.populationRequests && order.populationRequests.length > 0 && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="font-medium mb-3">
                Populations ({order.populationRequests.length}) :
              </div>
              <div className="space-y-2">
                {order.populationRequests.map((pop) => (
                  <div
                    key={pop.population}
                    className="flex justify-between items-center border-l-2 border-primary pl-3"
                  >
                    <div className="text-sm">
                      <span className="font-semibold">{pop.population}</span>
                      <span className="text-muted-foreground"> — {populationTypeLabel(pop.idType)}</span>
                    </div>
                    <span className="text-sm font-semibold">
                      {pop.volume.toLocaleString('fr-FR')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Segments sélectionnés */}
          {order.selectedSegments && order.selectedSegments.length > 0 && (
            <div className="bg-muted/50 p-4 rounded-lg">
              <div className="font-medium mb-3">Segments sélectionnés :</div>
              <div className="space-y-3">
                {Object.entries(segmentsByCategory).map(([categoryName, segments]) => (
                  <div key={categoryName} className="border-l-2 border-primary pl-3">
                    <div className="font-semibold text-sm text-muted-foreground mb-1">
                      {categoryName}
                    </div>
                    <ul className="space-y-1">
                      {segments.map((seg) => (
                        <li key={seg.segmentId} className="text-sm">
                          • {seg.segmentName}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message si aucune information disponible */}
          {!order.selectedCrm && (!order.selectedSegments || order.selectedSegments.length === 0) && (!order.populationRequests || order.populationRequests.length === 0) && (
            <div className="text-center text-muted-foreground py-4">
              Aucune information de configuration disponible pour cette demande.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

